import React, { useReducer, ReactNode } from "react";
import { useContextSelector, createContext } from "use-context-selector";

import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
import { UuidT } from "../../../flatbuffers/generated/front-end/uuid";
import { bankKey, liftKey, BankKey, LiftKey } from "../../../utils/typedKeys";

interface State {
  byBank: { [key: BankKey]: SocketMessageInterface };
  byLift: { [key: LiftKey]: SocketMessageInterface };
  bitmask: { [key: string]: boolean };
  positionMap: { [key: string]: number };
  serviceMap: { [key: string]: string };
  debounceMap: Map<string, number>;
  last?: SocketMessageInterface;
}

type Action =
  | { type: "UPDATE_MESSAGE"; payload: SocketMessageInterface }
  | { type: "UPDATE_DEBOUNCE_MAP"; payload: Map<string, number> }
  | { type: "CLEAR_MESSAGES" };

const BankMessagesContext = createContext<State | undefined>(undefined);
const BankMessagesDispatch = createContext<React.Dispatch<Action> | undefined>(
  undefined
);

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "UPDATE_MESSAGE": {
      const msg = action.payload;
      const bk = bankKey(msg.bankId);
      const lk = liftKey(msg.liftId);
      return {
        byBank: { ...state.byBank, [bk]: msg },
        byLift: { ...state.byLift, [lk]: msg },
        bitmask: { ...state.bitmask, ...msg.bitmask },
        positionMap: { ...state.positionMap, ...msg.positionMap },
        serviceMap: { ...state.serviceMap, ...msg.serviceMap },
        last: msg,
      };
    }
    case "UPDATE_DEBOUNCE_MAP": {
      return { ...state, debounceMap: action.payload };
    }
    case "CLEAR_MESSAGES": {
      return {
        byBank: {},
        byLift: {},
        bitmask: {},
        positionMap: {},
        serviceMap: {},
        debounceMap: state.debounceMap,
        last: undefined,
      };
    }
    default:
      return state;
  }
}

export const BankMessagesProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, {
    byBank: {},
    byLift: {},
    bitmask: {},
    positionMap: {},
    serviceMap: {},
    debounceMap: new Map(),
    last: undefined,
  });

  return (
    <BankMessagesContext.Provider value={state}>
      <BankMessagesDispatch.Provider value={dispatch}>
        {children}
      </BankMessagesDispatch.Provider>
    </BankMessagesContext.Provider>
  );
};

export function useLastBankMessage(
  bankId: UuidT
): SocketMessageInterface | undefined {
  return useContextSelector(
    BankMessagesContext,
    (ctx) => ctx?.byBank[bankKey(bankId)]
  );
}

export function useLastLiftMessage(
  liftId: UuidT
): SocketMessageInterface | undefined {
  return useContextSelector(
    BankMessagesContext,
    (ctx) => ctx?.byLift[liftKey(liftId)]
  );
}

export function useAllLiftMessages(): {
  [key: string]: SocketMessageInterface;
} {
  return useContextSelector(BankMessagesContext, (ctx) => ctx?.byLift) || {};
}

export function useLastSocketMessage(): SocketMessageInterface | undefined {
  return useContextSelector(BankMessagesContext, (ctx) => ctx?.last);
}

export function useBankDispatch() {
  return useContextSelector(BankMessagesDispatch, (dispatch) => {
    if (!dispatch)
      throw new Error(
        "useBankDispatch must be used within BankMessagesProvider"
      );
    return dispatch;
  });
}

export function useBitmask(): { [key: string]: boolean } {
  return useContextSelector(BankMessagesContext, (ctx) => ctx?.bitmask) || {};
}

export function usePositionMap(): { [key: string]: number } {
  return (
    useContextSelector(BankMessagesContext, (ctx) => ctx?.positionMap) || {}
  );
}

export function useServiceMap(): { [key: string]: string } {
  return (
    useContextSelector(BankMessagesContext, (ctx) => ctx?.serviceMap) || {}
  );
}

export function useDebounceMap(): Map<string, number> {
  return (
    useContextSelector(BankMessagesContext, (ctx) => ctx?.debounceMap) ||
    new Map()
  );
}

export function useClearBankMessages() {
  const dispatch = useBankDispatch();
  return () => dispatch({ type: "CLEAR_MESSAGES" });
}
